/**
 * VersionHistory component for displaying essay version timeline.
 * 
 * Features:
 * - Timeline view of all versions (Ant Design Timeline)
 * - Version number, date, and author
 * - Change summary and word count change
 * - Click to view version content
 * - Compare button to show diff
 * - Revert button (author only)
 * 
 * Called by:
 * - EssayDetailPage.tsx via drawer/modal
 */
import { useState } from 'react';
import {
  Timeline,
  Typography,
  Space,
  Button,
  Avatar,
  Tag,
  Spin,
  Empty,
  Modal,
  message,
  Tooltip,
} from 'antd';
import {
  UserOutlined,
  HistoryOutlined,
  DiffOutlined,
  RollbackOutlined,
  FileTextOutlined,
  PlusOutlined,
  MinusOutlined,
} from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { EssayVersionWithUser, User } from '@compendium/shared';
import { getVersions, revertToVersion } from '../services/api';
import StatusBadge from './StatusBadge';
import DiffViewer from './DiffViewer';

const { Text, Paragraph } = Typography;

// =============================================================================
// Component Props
// =============================================================================

interface VersionHistoryProps {
  /** Essay slug for API calls */
  slug: string;
  /** Current user (to check role for revert) */
  user: User;
  /** Callback when essay is reverted */
  onRevert?: () => void;
}

// =============================================================================
// Component
// =============================================================================

/**
 * VersionHistory component.
 * Displays timeline of essay versions with actions.
 */
function VersionHistory({ slug, user, onRevert }: VersionHistoryProps) {
  const queryClient = useQueryClient();
  
  // State for diff modal
  const [diffModalVisible, setDiffModalVisible] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  
  // State for content preview modal
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewVersion, setPreviewVersion] = useState<EssayVersionWithUser | null>(null);
  
  // State for revert confirmation
  const [revertLoading, setRevertLoading] = useState<number | null>(null);

  // Fetch versions
  const {
    data: versions = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['versions', slug],
    queryFn: () => getVersions(slug),
  });

  // Check if user is author (can revert)
  const isAuthor = user.role === 'author';

  /**
   * Format date for display.
   */
  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Format word count change.
   */
  function formatWordCountChange(current: number, previous: number | undefined): React.ReactNode {
    if (previous === undefined) {
      return (
        <Tag color="default" style={{ fontSize: '11px' }}>
          {current} words
        </Tag>
      );
    }
    
    const diff = current - previous;
    if (diff === 0) return null;
    
    return (
      <Tag
        color={diff > 0 ? 'green' : 'red'}
        style={{ fontSize: '11px' }}
        icon={diff > 0 ? <PlusOutlined /> : <MinusOutlined />}
      >
        {Math.abs(diff)} words
      </Tag>
    );
  }

  /**
   * Handle view diff click.
   */
  const handleViewDiff = (versionId: number) => {
    setSelectedVersionId(versionId);
    setDiffModalVisible(true);
  };

  /**
   * Handle view content click.
   */
  const handleViewContent = (version: EssayVersionWithUser) => {
    setPreviewVersion(version);
    setPreviewModalVisible(true);
  };

  /**
   * Handle revert click.
   */
  const handleRevert = async (version: EssayVersionWithUser) => {
    Modal.confirm({
      title: 'Revert to this version?',
      content: (
        <div>
          <p>
            This will restore the essay content to version {version.versionNumber}.
          </p>
          <p style={{ color: '#888' }}>
            A new version will be created documenting the revert.
          </p>
        </div>
      ),
      okText: 'Revert',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setRevertLoading(version.id);
        try {
          await revertToVersion(slug, version.id);
          message.success(`Reverted to version ${version.versionNumber}`);
          // Refresh versions list
          queryClient.invalidateQueries({ queryKey: ['versions', slug] });
          // Notify parent to refresh essay
          onRevert?.();
        } catch (error) {
          console.error('Failed to revert:', error);
          message.error(error instanceof Error ? error.message : 'Failed to revert');
        } finally {
          setRevertLoading(null);
        }
      },
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '32px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Empty
        description={error instanceof Error ? error.message : 'Failed to load versions'}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  // Empty state
  if (versions.length === 0) {
    return (
      <Empty
        description="No version history available"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  // Build timeline items
  const timelineItems = versions.map((version, index) => {
    const previousVersion = versions[index + 1];
    const isLatest = index === 0;
    
    return {
      key: version.id,
      dot: isLatest ? (
        <HistoryOutlined style={{ fontSize: '16px', color: '#d4af37' }} />
      ) : undefined,
      children: (
        <div style={{ paddingBottom: '16px' }}>
          {/* Version header */}
          <Space size="small" wrap style={{ marginBottom: '8px' }}>
            <Text strong>Version {version.versionNumber}</Text>
            {isLatest && <Tag color="gold">Current</Tag>}
            <StatusBadge status={version.status} size="small" />
            {formatWordCountChange(version.wordCount, previousVersion?.wordCount)}
          </Space>

          {/* Change summary */}
          {version.changeSummary && (
            <Paragraph
              style={{
                margin: '4px 0',
                color: '#d9d9d9',
                fontSize: '13px',
              }}
              ellipsis={{ rows: 2 }}
            >
              {version.changeSummary}
            </Paragraph>
          )}

          {/* Author and date */}
          <Space size="small" style={{ marginBottom: '8px' }}>
            <Avatar
              src={version.createdBy.avatarUrl}
              icon={!version.createdBy.avatarUrl && <UserOutlined />}
              size="small"
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {version.createdBy.name}
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              •
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {formatDate(version.createdAt)}
            </Text>
          </Space>

          {/* Action buttons */}
          <div>
            <Space size="small">
              <Tooltip title="View content">
                <Button
                  size="small"
                  type="text"
                  icon={<FileTextOutlined />}
                  onClick={() => handleViewContent(version)}
                >
                  View
                </Button>
              </Tooltip>

              {/* Only show diff if not the first version */}
              {previousVersion && (
                <Tooltip title="Compare with previous version">
                  <Button
                    size="small"
                    type="text"
                    icon={<DiffOutlined />}
                    onClick={() => handleViewDiff(version.id)}
                  >
                    Diff
                  </Button>
                </Tooltip>
              )}

              {/* Only show revert for author and not the latest version */}
              {isAuthor && !isLatest && (
                <Tooltip title="Revert to this version">
                  <Button
                    size="small"
                    type="text"
                    danger
                    icon={<RollbackOutlined />}
                    loading={revertLoading === version.id}
                    onClick={() => handleRevert(version)}
                  >
                    Revert
                  </Button>
                </Tooltip>
              )}
            </Space>
          </div>
        </div>
      ),
    };
  });

  return (
    <>
      {/* Version timeline */}
      <Timeline items={timelineItems} />

      {/* Diff modal */}
      <Modal
        title="Version Comparison"
        open={diffModalVisible}
        onCancel={() => setDiffModalVisible(false)}
        footer={null}
        width={800}
        styles={{
          body: { maxHeight: '70vh', overflow: 'auto' },
        }}
      >
        {selectedVersionId && (
          <DiffViewer slug={slug} versionId={selectedVersionId} />
        )}
      </Modal>

      {/* Content preview modal */}
      <Modal
        title={
          previewVersion
            ? `Version ${previewVersion.versionNumber} Content`
            : 'Version Content'
        }
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        footer={null}
        width={800}
        styles={{
          body: { maxHeight: '70vh', overflow: 'auto' },
        }}
      >
        {previewVersion && (
          <div>
            <Space style={{ marginBottom: '16px' }}>
              <StatusBadge status={previewVersion.status} />
              <Tag>{previewVersion.wordCount} words</Tag>
              <Text type="secondary">
                {formatDate(previewVersion.createdAt)}
              </Text>
            </Space>
            <pre
              style={{
                background: '#1a1a1a',
                padding: '16px',
                borderRadius: '8px',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                fontSize: '13px',
                lineHeight: 1.6,
              }}
            >
              {previewVersion.content}
            </pre>
          </div>
        )}
      </Modal>
    </>
  );
}

export default VersionHistory;
